import React, { useEffect, useState, useCallback } from "react";
import { auth } from "@/config/firebase";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { Users, Coins, Tag, ClipboardCheck, Clock, Image as ImageIcon, Megaphone, Lock, Unlock } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || "";
const SEASON = "2026-27";

async function call(path, method = "GET", body = null) {
  const token = await auth.currentUser.getIdToken();
  const res = await fetch(`${API}${path}`, {
    method,
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.detail || `Fehler ${res.status}`);
  return data;
}

const Section = ({ icon: Icon, title, children, testId }) => (
  <Card className="bg-[#12080c] border-white/10" data-testid={testId}>
    <CardHeader className="pb-3">
      <CardTitle className="text-base flex items-center gap-2 text-amber-300"><Icon size={18} /> {title}</CardTitle>
    </CardHeader>
    <CardContent className="space-y-3">{children}</CardContent>
  </Card>
);

export default function MarketOpsPanel() {
  const [participants, setParticipants] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [values, setValues] = useState({ startingCoins: 100, marketBonus: 20, correctGuessCoins: 10 });
  const [audit, setAudit] = useState(null);
  const [aliases, setAliases] = useState(null);
  const [preflight, setPreflight] = useState(null);
  const [live, setLive] = useState(null);
  const [betStatus, setBetStatus] = useState(null);
  const [win, setWin] = useState({ opensAt: "", closesAt: "" });
  const [busy, setBusy] = useState("");

  const loadParticipants = useCallback(async () => {
    const d = await call(`/api/market-ops/participants?season_id=${SEASON}`);
    setParticipants(d.users);
    setSelected(new Set(d.selectedIds));
  }, []);

  const loadBetStatus = useCallback(() => {
    call(`/api/economy/admin/betting-status`).then(setBetStatus).catch(() => {});
  }, []);

  useEffect(() => {
    loadParticipants().catch((e) => toast.error(e.message));
    call(`/api/market-ops/live-audit`).then(setLive).catch(() => {});
    loadBetStatus();
  }, [loadParticipants, loadBetStatus]);

  const toggle = (uid) => {
    const next = new Set(selected);
    next.has(uid) ? next.delete(uid) : next.add(uid);
    setSelected(next);
  };

  const run = async (label, fn) => {
    setBusy(label);
    try { await fn(); } catch (e) { toast.error(e.message); } finally { setBusy(""); }
  };

  return (
    <div className="min-h-screen bg-[#0a0608] text-white p-4 md:p-8" data-testid="market-ops-panel">
      <div className="max-w-5xl mx-auto space-y-5">
        <div>
          <h1 className="text-3xl font-black text-amber-400">Markt & Coins — Nachbereitung</h1>
          <p className="text-white/50 text-sm mt-1">Audit-first. Nichts wird blind gebucht. Der Markt wird erst am Ende bewusst geöffnet.</p>
        </div>

        {/* 1. Live audit */}
        <Section icon={ClipboardCheck} title="1. Live-Daten-Audit" testId="mo-live-audit">
          {!live ? <p className="text-white/40 text-sm">…</p> : (
            <div className="text-sm space-y-1">
              <p className="text-white/60">{live.sessions.length} Session(s), {live.totalIdentityGuesses} Tipps, {live.totalWagers} Wetten</p>
              {live.sessions.map((s) => (
                <div key={s.sessionId} className="flex justify-between bg-white/5 rounded px-3 py-1.5">
                  <span>{s.name || s.sessionId} <span className="text-white/40">({s.phase})</span></span>
                  <span className="text-amber-300">{s.participants} Teilnehmer</span>
                </div>
              ))}
            </div>
          )}
        </Section>

        {/* Wettbeteiligung: wer hat noch nicht alles gesetzt */}
        <Section icon={Coins} title="Wettbeteiligung — wer hat noch Coins frei?" testId="mo-bet-status">
          {!betStatus ? <p className="text-white/40 text-sm">…</p> : (
            <>
              <div className="grid grid-cols-3 gap-2 mb-3">
                <div className="rounded-lg bg-red-500/10 border border-red-500/30 p-2.5 text-center">
                  <div className="text-xl font-bold text-red-300" data-testid="bet-status-notdone">{betStatus.notDoneCount}</div>
                  <div className="text-[11px] text-white/60">noch offen</div>
                </div>
                <div className="rounded-lg bg-green-500/10 border border-green-500/30 p-2.5 text-center">
                  <div className="text-xl font-bold text-green-300">{betStatus.doneCount}</div>
                  <div className="text-[11px] text-white/60">alles gesetzt</div>
                </div>
                <div className="rounded-lg bg-yellow-500/10 border border-yellow-500/30 p-2.5 text-center">
                  <div className="text-xl font-bold text-yellow-300">{Math.round(betStatus.totalFree)}</div>
                  <div className="text-[11px] text-white/60">Coins frei gesamt</div>
                </div>
              </div>
              <div className="max-h-80 overflow-auto pr-1 space-y-1">
                {betStatus.users.map((u) => (
                  <div key={u.userId} className={`flex items-center justify-between rounded px-3 py-1.5 text-sm ${u.hasBetAll ? 'bg-white/5' : 'bg-red-500/10 border border-red-500/20'}`} data-testid={`bet-status-row-${u.userId}`}>
                    <span className="truncate flex items-center gap-2">
                      {u.hasBetAll ? <span className="text-green-400">✓</span> : <span className="text-red-400">●</span>}
                      {u.displayName}
                    </span>
                    <span className="flex items-center gap-2 text-xs shrink-0">
                      {u.hasBetAll
                        ? <span className="text-white/40">alles gesetzt</span>
                        : <span className="text-red-300 font-bold">{Math.round(u.free)} frei</span>}
                      {u.earned > 0 && <span className="text-indigo-300">· ⚡{Math.round(u.earned)}</span>}
                      {u.reserved > 0 && <span className="text-white/40">· {Math.round(u.reserved)} gesetzt</span>}
                    </span>
                  </div>
                ))}
              </div>
              <button onClick={loadBetStatus} className="text-[11px] text-amber-300/70 hover:text-amber-300 underline mt-1" data-testid="bet-status-refresh">Aktualisieren</button>
            </>
          )}
        </Section>

        {/* 2. Participants */}
        <Section icon={Users} title="2. Marktteilnehmer auswählen" testId="mo-participants">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-72 overflow-auto pr-1">
            {participants?.map((u) => (
              <label key={u.userId} className={`flex items-center gap-2 rounded px-2 py-1.5 cursor-pointer text-sm ${selected.has(u.userId) ? "bg-amber-500/20" : "bg-white/5"}`}>
                <input type="checkbox" checked={selected.has(u.userId)} onChange={() => toggle(u.userId)} data-testid={`mo-user-${u.userId}`} />
                <span className="truncate">{u.displayName}</span>
              </label>
            ))}
          </div>
          <Button data-testid="mo-save-participants" disabled={busy === "part"} onClick={() => run("part", async () => {
            await call(`/api/market-ops/participants`, "POST", { seasonId: SEASON, userIds: Array.from(selected) });
            toast.success(`${selected.size} Teilnehmer gespeichert`);
          })} className="bg-amber-600 hover:bg-amber-700">{selected.size} Teilnehmer speichern</Button>
        </Section>

        {/* 3. Coin audit */}
        <Section icon={Coins} title="3. Coin-Audit (Dry-Run) & Buchung" testId="mo-coins">
          <div className="flex flex-wrap gap-3">
            {["startingCoins", "marketBonus", "correctGuessCoins"].map((k) => (
              <label key={k} className="text-xs text-white/60">{k}
                <Input type="number" value={values[k]} onChange={(e) => setValues({ ...values, [k]: parseInt(e.target.value) || 0 })} className="bg-white/5 border-white/10 w-28 mt-1" data-testid={`mo-val-${k}`} />
              </label>
            ))}
          </div>
          <div className="flex gap-2">
            <Button data-testid="mo-dryrun" disabled={busy === "dry"} onClick={() => run("dry", async () => {
              setAudit(await call(`/api/market-ops/coin-audit?season_id=${SEASON}`, "POST", values));
            })} className="bg-blue-600 hover:bg-blue-700">Dry-Run berechnen</Button>
            {audit && (
              <Button data-testid="mo-apply" disabled={busy === "apply"} onClick={() => run("apply", async () => {
                const r = await call(`/api/market-ops/coin-apply?season_id=${SEASON}`, "POST", values);
                toast.success(`Gebucht: ${r.applied.coinsCredited} Coins (Start ${r.applied.startBackfill}, Rewards ${r.applied.rewardBackpay}, Bonus ${r.applied.marketBonus})`);
                setAudit(await call(`/api/market-ops/coin-audit?season_id=${SEASON}`, "POST", values));
              })} className="bg-green-600 hover:bg-green-700">Korrekturen anwenden</Button>
            )}
          </div>
          {audit && (
            <div className="text-xs">
              <div className="text-white/60 mb-2">Summe: Start {audit.totals.missingStart} · Rewards {audit.totals.missingRewards} · Bonus {audit.totals.marketBonus} · <span className="text-amber-300">gesamt {audit.totals.grandTotal}</span></div>
              <div className="overflow-auto max-h-72">
                <table className="w-full text-left">
                  <thead className="text-white/40"><tr><th className="py-1">Spieler</th><th>Kontostand</th><th>+Start</th><th>+Rewards</th><th>+Bonus</th><th>→ neu</th></tr></thead>
                  <tbody>
                    {audit.rows.map((r) => (
                      <tr key={r.userId} className="border-t border-white/5">
                        <td className="py-1">{r.displayName}</td>
                        <td>{Math.round(r.currentBalance)}</td>
                        <td className={r.missingStart ? "text-green-400" : "text-white/30"}>{r.missingStart}</td>
                        <td className={r.missingRewards ? "text-green-400" : "text-white/30"}>{r.missingRewards}</td>
                        <td className={r.marketBonus ? "text-green-400" : "text-white/30"}>{r.marketBonus}</td>
                        <td className="text-amber-300">{Math.round(r.projectedBalance)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </Section>

        {/* 4. Alias review */}
        <Section icon={Tag} title="4. Alias-Review (nicht erkannte Antworten)" testId="mo-alias">
          <Button data-testid="mo-load-alias" disabled={busy === "alias"} onClick={() => run("alias", async () => {
            setAliases(await call(`/api/market-ops/alias-review?season_id=${SEASON}`));
          })} className="bg-blue-600 hover:bg-blue-700">Antworten laden</Button>
          {aliases && (
            <div className="space-y-1 max-h-72 overflow-auto text-sm">
              <p className="text-white/50 text-xs">{aliases.totalGroups} Gruppen. Bestätige nur echte Schreibweisen derselben Person.</p>
              {aliases.unrecognized.map((g, i) => (
                <div key={i} className="flex items-center justify-between bg-white/5 rounded px-3 py-1.5">
                  <span><b className="text-amber-300">{g.answer}</b> → {g.roundUserName} <span className="text-white/40">×{g.count}</span></span>
                  <Button size="sm" className="h-7 bg-green-600 hover:bg-green-700 text-xs" onClick={() => run("alias", async () => {
                    const r = await call(`/api/market-ops/alias-confirm`, "POST", { seasonId: SEASON, roundUserId: g.roundUserId, alias: g.answer, correctGuessCoins: values.correctGuessCoins });
                    toast.success(`Alias bestätigt: ${r.backpaid} Rewards (${r.coinsCredited} Coins)`);
                    setAliases(await call(`/api/market-ops/alias-review?season_id=${SEASON}`));
                  })}>Als Alias bestätigen</Button>
                </div>
              ))}
            </div>
          )}
        </Section>

        {/* 5. Preflight */}
        <Section icon={ClipboardCheck} title="5. Prediction-Preflight" testId="mo-preflight">
          <Button data-testid="mo-preflight-btn" disabled={busy === "pf"} onClick={() => run("pf", async () => {
            setPreflight(await call(`/api/market-ops/preflight?season_id=${SEASON}`));
          })} className="bg-blue-600 hover:bg-blue-700">Preflight prüfen</Button>
          {preflight && (
            <div className="text-sm">
              <p className={preflight.ready ? "text-green-400" : "text-red-400"}>
                {preflight.totalPredictions} Predictions · {preflight.ready ? "✓ alle marktbereit" : `${preflight.issuesCount} mit Problemen`}
              </p>
              {preflight.issues.map((i) => (
                <div key={i.id} className="text-xs text-red-300/80">• {i.text?.slice(0, 40)}: {i.issues.join(", ")}</div>
              ))}
            </div>
          )}
        </Section>

        {/* 6. Window */}
        <Section icon={Clock} title="6. Wettfenster festlegen (Europe/Berlin)" testId="mo-window">
          <div className="flex flex-wrap gap-3">
            <label className="text-xs text-white/60">Öffnen
              <Input type="datetime-local" value={win.opensAt} onChange={(e) => setWin({ ...win, opensAt: e.target.value })} className="bg-white/5 border-white/10 mt-1" data-testid="mo-opens" />
            </label>
            <label className="text-xs text-white/60">Schließen
              <Input type="datetime-local" value={win.closesAt} onChange={(e) => setWin({ ...win, closesAt: e.target.value })} className="bg-white/5 border-white/10 mt-1" data-testid="mo-closes" />
            </label>
          </div>
          <Button data-testid="mo-save-window" disabled={busy === "win"} onClick={() => run("win", async () => {
            if (!win.opensAt || !win.closesAt) throw new Error("Bitte beide Zeitpunkte setzen");
            await call(`/api/market-ops/window`, "POST", { seasonId: SEASON, opensAt: new Date(win.opensAt).toISOString(), closesAt: new Date(win.closesAt).toISOString() });
            toast.success("Wettfenster gespeichert");
          })} className="bg-amber-600 hover:bg-amber-700">Fenster speichern</Button>
        </Section>

        {/* 7. Open / close + announce */}
        <Section icon={Megaphone} title="7. Markt öffnen/schließen & Ansage" testId="mo-market">
          <p className="text-xs text-yellow-400/80">⚠️ Der Markt wird bewusst manuell geöffnet. Erst wenn Audit, Coins & Preflight passen.</p>
          <div className="flex flex-wrap gap-2">
            <Button data-testid="mo-open-market" disabled={busy === "open"} onClick={() => run("open", async () => {
              await call(`/api/predictions-market/admin/open-market`, "POST", { seasonId: SEASON });
              await call(`/api/market-ops/announce`, "POST", { seasonId: SEASON, kind: "open" });
              toast.success("Markt geöffnet + Chat-Ansage");
            })} className="bg-green-600 hover:bg-green-700"><Unlock size={14} className="mr-1" /> Markt öffnen</Button>
            <Button data-testid="mo-close-market" disabled={busy === "close"} onClick={() => run("close", async () => {
              await call(`/api/predictions-market/admin/close-market`, "POST", { seasonId: SEASON });
              await call(`/api/market-ops/announce`, "POST", { seasonId: SEASON, kind: "close" });
              toast.success("Markt geschlossen + Chat-Ansage");
            })} className="bg-red-600 hover:bg-red-700"><Lock size={14} className="mr-1" /> Markt schließen</Button>
            <Button data-testid="mo-remind" disabled={busy === "rem"} onClick={() => run("rem", async () => {
              await call(`/api/market-ops/announce`, "POST", { seasonId: SEASON, kind: "reminder24" });
              toast.success("24-Std-Hinweis gesendet");
            })} variant="outline" className="border-white/20">Noch 24 Std ansagen</Button>
          </div>
          <div className="mt-3 pt-3 border-t border-white/10">
            <p className="text-xs text-orange-300/90 mb-2">🔔 <b>Phase 1 abschließen:</b> nicht verwettetes Guthaben verfällt, Quoten werden neu berechnet und ab dann <b>dynamisch/live</b> (nur noch frisch verdiente Coins wettbar).</p>
            <Button data-testid="mo-close-phase1" disabled={busy === "phase1"} onClick={() => run("phase1", async () => {
              if (!window.confirm("Phase 1 jetzt abschließen? Nicht verwettetes Guthaben verfällt unwiderruflich und die Quoten werden neu berechnet (Phase 2 = dynamische Quoten).")) return;
              const r = await call(`/api/predictions-market/admin/close-phase1`, "POST", { seasonId: SEASON });
              toast.success(`Phase 1 abgeschlossen: ${Math.round(r.totalExpired)} verfallen, ${Math.round(r.totalActivated||0)} verdiente Coins freigeschaltet, ${r.predictionsRepriced} Quoten neu (Ø ${r.referenceAvgStake})`);
            })} className="bg-orange-600 hover:bg-orange-700"><Lock size={14} className="mr-1" /> Phase 1 abschließen → Phase 2 starten</Button>
          </div>
        </Section>

        {/* 8. Media index */}
        <Section icon={ImageIcon} title="8. Media-Bibliothek indexieren" testId="mo-media">
          <Button data-testid="mo-media-index" disabled={busy === "media"} onClick={() => run("media", async () => {
            const r = await call(`/api/market-ops/media-index?season_id=${SEASON}`, "POST");
            toast.success(`Media indexiert: ${r.newlyIndexed} neu von ${r.sourcesSeen}`);
          })} className="bg-blue-600 hover:bg-blue-700">Index aufbauen/aktualisieren</Button>
        </Section>
      </div>
    </div>
  );
}
